import { type CalibreImportResultDto, isTerminalJobStatus, type JobDto } from "@shelflist/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { CircleAlert } from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { ConfirmDialog } from "@/components/confirm-dialog";
import { JobLog } from "@/components/jobs/job-log";
import { JobStatusBadge } from "@/components/jobs/status-badge";
import { Page } from "@/components/page";
import { EmptyState, LoadingState } from "@/components/states";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { useFormat } from "@/i18n/format";
import { ApiError, api, call } from "@/lib/api";
import { useErrorMessage } from "@/lib/errors";
import { jobDuration, jobKeys, jobQuery, useJobTypeLabel } from "@/lib/jobs";

export const Route = createFileRoute("/_app/admin/jobs/$jobId")({
  component: JobPage,
});

const BYTE_KEYS = new Set(["copiedBytes"]);

function JobPage() {
  const { jobId } = Route.useParams();
  const { t } = useTranslation("jobs");
  const errorMessage = useErrorMessage();
  const { data: job, error } = useQuery({ ...jobQuery(jobId), retry: false });

  if (error instanceof ApiError && error.code === "job_not_found") {
    return (
      <Page title={t("detail.title")}>
        <EmptyState title={t("detail.notFound")} action={<BackLink />} />
      </Page>
    );
  }
  if (error && !job) {
    return (
      <p role="alert" className="text-destructive">
        {errorMessage(error)}
      </p>
    );
  }
  if (!job) return <LoadingState />;
  return <JobDetail job={job} />;
}

function BackLink() {
  const { t } = useTranslation("jobs");
  return (
    <Button asChild variant="link" className="px-0">
      <Link to="/admin/jobs">{t("back")}</Link>
    </Button>
  );
}

export function JobDetail({ job }: { job: JobDto }) {
  const { t, i18n } = useTranslation("jobs");
  const format = useFormat();
  const typeLabel = useJobTypeLabel();
  const errorMessage = useErrorMessage();
  const queryClient = useQueryClient();
  const terminal = isTerminalJobStatus(job.status);
  const cancel = useMutation({
    mutationFn: () => call(api.admin.jobs[":id"].cancel.$post({ param: { id: job.id } })),
    onSuccess: (data) => {
      toast.success(
        data.job.status === "cancelled"
          ? t("detail.cancelQueued")
          : t("detail.cancelRequestedToast"),
      );
      return queryClient.invalidateQueries({ queryKey: jobKeys.all });
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  const percent =
    job.progress?.total && job.progress.total > 0
      ? Math.min(100, Math.round((job.progress.processed / job.progress.total) * 100))
      : undefined;
  const phaseLabel = job.progress
    ? i18n.exists(`jobs:phase.${job.progress.phase}`)
      ? t(`phase.${job.progress.phase as "books"}`)
      : job.progress.phase
    : undefined;
  const result = (job.result ?? {}) as Partial<CalibreImportResultDto> & Record<string, unknown>;
  // A failed or cancelled job reports only what it got done; zero values would be noise.
  const hideZeros = job.status === "failed" || job.status === "cancelled";
  const resultEntries = Object.entries(result).filter(
    (entry): entry is [string, number] =>
      typeof entry[1] === "number" && !(hideZeros && entry[1] === 0),
  );
  const payload = (job.payload ?? {}) as Record<string, unknown>;

  return (
    <Page
      title={typeLabel(job.type)}
      description={<JobStatusBadge job={job} />}
      actions={
        terminal || job.cancelRequested ? null : (
          <ConfirmDialog
            trigger={
              <Button variant="outline" disabled={cancel.isPending}>
                {t("detail.cancel")}
              </Button>
            }
            title={t("detail.cancelTitle")}
            description={t("detail.cancelDescription")}
            confirmLabel={t("detail.cancelConfirm")}
            destructive
            onConfirm={() => cancel.mutate()}
          />
        )
      }
    >
      {job.error ? (
        <Alert variant="destructive">
          <CircleAlert aria-hidden />
          <AlertTitle>{t("detail.error")}</AlertTitle>
          <AlertDescription>
            {i18n.exists(`jobs:errors.${job.error}`)
              ? t(`errors.${job.error as "storage_full"}`)
              : job.error}
          </AlertDescription>
        </Alert>
      ) : null}

      <section aria-labelledby="job-progress-title" className="grid gap-3">
        <h2 id="job-progress-title" className="text-lg font-semibold">
          {t("detail.progress")}
        </h2>
        {job.status === "queued" ? (
          <p className="text-muted-foreground">{t("detail.waiting")}</p>
        ) : job.progress ? (
          <div className="grid gap-2">
            <div className="flex flex-wrap justify-between gap-2 text-sm">
              <span>{phaseLabel}</span>
              <span className="text-muted-foreground">
                {job.progress.total !== null
                  ? t("detail.progressCount", {
                      processed: format.number(job.progress.processed),
                      total: format.number(job.progress.total),
                    })
                  : t("detail.progressUnknown", {
                      processed: format.number(job.progress.processed),
                    })}
              </span>
            </div>
            <div
              role="progressbar"
              aria-label={t("detail.progress")}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={percent}
              className="bg-secondary h-2 overflow-hidden rounded-full"
            >
              <div
                className="bg-primary h-full transition-[width]"
                style={{ width: `${percent ?? (terminal ? 100 : 0)}%` }}
              />
            </div>
          </div>
        ) : null}
        <dl className="text-muted-foreground grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm">
          <dt>{t("detail.created")}</dt>
          <dd>{format.dateTime(job.createdAt)}</dd>
          {job.startedAt ? (
            <>
              <dt>{t("detail.started")}</dt>
              <dd>{format.dateTime(job.startedAt)}</dd>
            </>
          ) : null}
          {job.finishedAt ? (
            <>
              <dt>{t("detail.finished")}</dt>
              <dd>
                {format.dateTime(job.finishedAt)} ({jobDuration(job, format.duration)})
              </dd>
            </>
          ) : null}
          {Object.keys(payload).length > 0 ? (
            <>
              <dt>{t("detail.payload")}</dt>
              <dd>
                {Object.entries(payload)
                  .map(([key, value]) => {
                    const label = i18n.exists(`jobs:payloadKeys.${key}`)
                      ? t(`payloadKeys.${key as "path"}`)
                      : key;
                    const shown =
                      typeof value === "boolean"
                        ? t(value ? "payloadKeys.yes" : "payloadKeys.no")
                        : String(value);
                    return `${label}: ${shown}`;
                  })
                  .join(" · ")}
              </dd>
            </>
          ) : null}
        </dl>
      </section>

      {resultEntries.length > 0 ? (
        <section aria-labelledby="job-result-title" className="grid gap-3">
          <h2 id="job-result-title" className="text-lg font-semibold">
            {t("detail.result")}
          </h2>
          <dl className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {resultEntries.map(([key, value]) => (
              <div key={key} className="rounded-lg border px-3 py-2">
                <dt className="text-muted-foreground text-xs">
                  {i18n.exists(`jobs:result.${key}`) ? t(`result.${key as "imported"}`) : key}
                </dt>
                <dd className="text-xl font-semibold">
                  {BYTE_KEYS.has(key) ? format.bytes(value) : format.number(value)}
                </dd>
              </div>
            ))}
          </dl>
        </section>
      ) : null}

      <JobLog jobId={job.id} live={!terminal} />
      <div>
        <BackLink />
      </div>
    </Page>
  );
}
